"use client";

import { css } from "@emotion/react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import {
	ChevronDownIcon,
	Clock3Icon,
	InfoIcon,
	LoaderCircle,
} from "lucide-react";
import * as React from "react";
import type { DateRange } from "react-day-picker";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button, buttonVariants } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { useStorefrontSettingsState } from "@/hooks/useStorefrontSettingsState";
import {
	type TranslateFunction,
	useTranslate,
} from "@/hooks/useTranslate/useTranslate";
import { cn } from "@/lib/utils";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

export type TimeRange = {
	from: string | undefined;
	to?: string | undefined;
};

export type CatalogDurationRules = {
	minHours?: number | null;
	maxHours?: number | null;
};

export type DateTimeRangeValue = {
	dateRange: DateRange | undefined;
	timeRange: TimeRange | undefined;
};

const parseTimeToMinutes = (timeValue?: string) => {
	if (!timeValue) {
		return undefined;
	}
	const [hoursRaw, minutesRaw] = timeValue.split(":");
	const hours = Number.parseInt(hoursRaw ?? "", 10);
	const minutes = Number.parseInt(minutesRaw ?? "", 10);
	if (
		Number.isNaN(hours) ||
		Number.isNaN(minutes) ||
		hours < 0 ||
		hours > 23 ||
		minutes < 0 ||
		minutes > 59
	) {
		return undefined;
	}
	return hours * 60 + minutes;
};

const formatMinutesToTime = (minutes: number) => {
	const normalizedMinutes = ((minutes % 1440) + 1440) % 1440;
	const hours = Math.floor(normalizedMinutes / 60)
		.toString()
		.padStart(2, "0");
	const mins = (normalizedMinutes % 60).toString().padStart(2, "0");
	return `${hours}:${mins}`;
};

const parseDisplayTime = (timeValue?: string) => {
	if (!timeValue) {
		return undefined;
	}
	const normalizedValue = timeValue.trim().toUpperCase();
	const formats = ["HH:mm", "H:mm", "hh:mm A", "h:mm A"];
	for (const format of formats) {
		const parsedValue = dayjs(normalizedValue, format, true);
		if (parsedValue.isValid()) {
			return parsedValue.format("HH:mm");
		}
	}
	return undefined;
};

const formatTimeForDisplay = (
	timeValue: string | undefined,
	use24HourTime: boolean,
) => {
	if (!timeValue) {
		return "";
	}
	const parsedValue = dayjs(timeValue, "HH:mm", true);
	if (!parsedValue.isValid()) {
		return timeValue;
	}
	return parsedValue.format(use24HourTime ? "HH:mm" : "hh:mm A");
};

const buildDateTime = (dateValue: Date | undefined, timeValue?: string) => {
	const dateTime = dayjs(dateValue);
	if (!dateTime.isValid()) {
		return dateTime;
	}
	const timeInMinutes = parseTimeToMinutes(timeValue);
	if (timeInMinutes === undefined) {
		return dateTime.startOf("day");
	}
	return dateTime
		.set("hour", Math.floor(timeInMinutes / 60))
		.set("minute", timeInMinutes % 60)
		.startOf("minute");
};

const isWithinOperatingHours = (
	timeInMinutes: number,
	openMinutes: number,
	closeMinutes: number,
) => {
	if (openMinutes <= closeMinutes) {
		return timeInMinutes >= openMinutes && timeInMinutes <= closeMinutes;
	}
	return timeInMinutes >= openMinutes || timeInMinutes <= closeMinutes;
};

const areDatesEqual = (left?: Date, right?: Date) => {
	if (!left && !right) {
		return true;
	}
	if (!left || !right) {
		return false;
	}
	return dayjs(left).isSame(dayjs(right), "day");
};

const getTimeParts = (timeValue: string | undefined) => {
	const minutes = parseTimeToMinutes(timeValue) ?? 0;
	const hour24 = Math.floor(minutes / 60);
	const minute = minutes % 60;
	const meridiem = hour24 >= 12 ? "PM" : "AM";
	const hour12 = hour24 % 12 || 12;
	return {
		hour24,
		hour12,
		minute,
		meridiem,
	};
};

const to24HourValue = (hour12: number, meridiem: "AM" | "PM") => {
	const normalizedHour = hour12 % 12;
	if (meridiem === "PM") {
		return normalizedHour + 12;
	}
	return normalizedHour;
};

const hourColumnValues12 = Array.from({ length: 12 }, (_, index) =>
	(index + 1).toString().padStart(2, "0"),
);
const hourColumnValues24 = Array.from({ length: 24 }, (_, index) =>
	index.toString().padStart(2, "0"),
);
const minuteColumnValues = Array.from({ length: 60 }, (_, index) =>
	index.toString().padStart(2, "0"),
);
const meridiemColumnValues = ["AM", "PM"] as const;

function CompactRangeCalendar({
	range,
	onFromSelect,
	onToSelect,
	disablePast,
	enableOpenDays,
	openDays,
	fromLabel,
	toLabel,
}: {
	range: DateRange | undefined;
	onFromSelect: (date: Date | undefined) => void;
	onToSelect: (date: Date | undefined) => void;
	disablePast: boolean;
	enableOpenDays: boolean;
	openDays: readonly boolean[];
	fromLabel: string;
	toLabel: string;
}) {
	const fromMonthKey = dayjs(range?.from ?? new Date()).format("YYYY-MM");
	const toMonthKey = dayjs(range?.to ?? range?.from ?? new Date()).format(
		"YYYY-MM",
	);
	const [fromMonth, setFromMonth] = React.useState(() =>
		dayjs(`${fromMonthKey}-01`).toDate(),
	);
	const [toMonth, setToMonth] = React.useState(() =>
		dayjs(`${toMonthKey}-01`).toDate(),
	);

	React.useEffect(() => {
		setFromMonth(dayjs(`${fromMonthKey}-01`).toDate());
	}, [fromMonthKey]);

	React.useEffect(() => {
		setToMonth(dayjs(`${toMonthKey}-01`).toDate());
	}, [toMonthKey]);

	const disabledDays = [
		...(disablePast ? [{ before: dayjs().startOf("day").toDate() }] : []),
		...(enableOpenDays
			? [
					{
						dayOfWeek: openDays.flatMap((isOpen, day) => (isOpen ? [] : [day])),
					},
				]
			: []),
	];
	const rangeModifiers = {
		...(range?.from ? { range_start: range.from } : {}),
		...(range?.to ? { range_end: range.to } : {}),
		...(range?.from && range?.to && dayjs(range.to).isAfter(range.from, "day")
			? { range_middle: { after: range.from, before: range.to } }
			: {}),
	};
	const calendarClassNames = {
		range_start: "rounded-md bg-muted",
		range_middle: "rounded-none bg-muted",
		range_end: "rounded-md bg-muted",
		disabled:
			"text-muted-foreground opacity-40 line-through decoration-destructive",
	};
	const calendarStartMonth = dayjs()
		.subtract(10, "year")
		.startOf("year")
		.toDate();
	const calendarEndMonth = dayjs().add(20, "year").endOf("year").toDate();

	return (
		<div
			data-compact-range-calendar
			className="hidden grid-cols-2 gap-3 md:grid"
		>
			<div className="min-w-0 rounded-md bg-muted/20 p-1">
				<div className="pt-1 text-center text-xs font-semibold">
					{fromLabel}
				</div>
				<Calendar
					mode="single"
					required
					selected={range?.from}
					onSelect={onFromSelect}
					month={fromMonth}
					onMonthChange={setFromMonth}
					captionLayout="dropdown"
					startMonth={calendarStartMonth}
					endMonth={calendarEndMonth}
					disabled={disabledDays}
					modifiers={rangeModifiers}
					showOutsideDays={false}
					className="w-full p-1 [--cell-size:--spacing(7)]"
					classNames={calendarClassNames}
				/>
			</div>
			<div className="min-w-0 rounded-md bg-muted/20 p-1">
				<div className="pt-1 text-center text-xs font-semibold">{toLabel}</div>
				<Calendar
					mode="single"
					required
					selected={range?.to}
					onSelect={onToSelect}
					month={toMonth}
					onMonthChange={setToMonth}
					captionLayout="dropdown"
					startMonth={calendarStartMonth}
					endMonth={calendarEndMonth}
					disabled={disabledDays}
					modifiers={rangeModifiers}
					showOutsideDays={false}
					className="w-full p-1 [--cell-size:--spacing(7)]"
					classNames={calendarClassNames}
				/>
			</div>
		</div>
	);
}

function DateField({
	id,
	value,
	disabled,
	disablePast,
	onChange,
}: {
	id: string;
	value: Date | undefined;
	disabled: boolean;
	disablePast: boolean;
	onChange: (value: Date | undefined) => void;
}) {
	return (
		<Input
			id={id}
			type="date"
			value={value ? dayjs(value).format("YYYY-MM-DD") : ""}
			min={disablePast ? dayjs().format("YYYY-MM-DD") : undefined}
			disabled={disabled}
			className="h-8 min-w-36 text-xs"
			onChange={(event) => {
				const nextValue = dayjs(event.currentTarget.value, "YYYY-MM-DD", true);
				onChange(
					nextValue.isValid() ? nextValue.startOf("day").toDate() : undefined,
				);
			}}
		/>
	);
}

function TimeField({
	id,
	value,
	disabled,
	onChange,
	translate,
	use24HourTime,
}: {
	id: string;
	value: string | undefined;
	disabled: boolean;
	onChange: (value: string | undefined) => void;
	translate: TranslateFunction;
	use24HourTime: boolean;
}) {
	const [open, setOpen] = React.useState(false);
	const [draftValue, setDraftValue] = React.useState(() =>
		formatTimeForDisplay(value, use24HourTime),
	);
	const [inputValue, setInputValue] = React.useState(() =>
		formatTimeForDisplay(value, use24HourTime),
	);
	const popupRef = React.useRef<HTMLDivElement>(null);
	const draftTimeValue = React.useMemo(
		() => parseDisplayTime(draftValue),
		[draftValue],
	);
	const timeParts = React.useMemo(
		() => getTimeParts(draftTimeValue ?? value),
		[draftTimeValue, value],
	);

	const resetDraftValue = React.useCallback(() => {
		const formattedValue = formatTimeForDisplay(value, use24HourTime);
		setDraftValue(formattedValue);
		setInputValue(formattedValue);
	}, [value, use24HourTime]);

	React.useEffect(() => {
		if (!open) {
			resetDraftValue();
		}
	}, [open, resetDraftValue]);

	React.useEffect(() => {
		if (!open) {
			return;
		}
		const selectedIds = [
			use24HourTime ? `hour-${timeParts.hour24}` : `hour-${timeParts.hour12}`,
			`minute-${timeParts.minute}`,
			`meridiem-${timeParts.meridiem}`,
		];
		selectedIds.forEach((selectedId) => {
			const selectedElement = popupRef.current?.querySelector<HTMLElement>(
				`[data-time-option="${selectedId}"]`,
			);
			selectedElement?.scrollIntoView({
				block: "center",
			});
		});
	}, [open, timeParts, use24HourTime]);

	const setNormalizedDraftValue = React.useCallback(
		(nextValue: string | undefined) => {
			setDraftValue(formatTimeForDisplay(nextValue, use24HourTime));
		},
		[use24HourTime],
	);

	const updateTimePart = React.useCallback(
		(partial: Partial<ReturnType<typeof getTimeParts>>) => {
			const nextHour24 = use24HourTime
				? (partial.hour24 ?? timeParts.hour24)
				: to24HourValue(
						partial.hour12 ?? timeParts.hour12,
						(partial.meridiem ?? timeParts.meridiem) as "AM" | "PM",
					);
			const nextMinute = partial.minute ?? timeParts.minute;
			setNormalizedDraftValue(
				formatMinutesToTime(nextHour24 * 60 + nextMinute),
			);
		},
		[setNormalizedDraftValue, timeParts, use24HourTime],
	);

	const commitDraftValue = React.useCallback(() => {
		if (!draftValue.trim()) {
			onChange(undefined);
			setInputValue("");
			setOpen(false);
			return;
		}
		const parsedValue = parseDisplayTime(draftValue);
		if (parsedValue) {
			setInputValue(formatTimeForDisplay(parsedValue, use24HourTime));
			onChange(parsedValue);
			setOpen(false);
			return;
		}
		resetDraftValue();
	}, [draftValue, onChange, resetDraftValue, use24HourTime]);

	return (
		<Popover
			modal={false}
			open={open}
			onOpenChange={(nextOpen) => {
				if (!nextOpen) {
					resetDraftValue();
				}
				setOpen(nextOpen);
			}}
		>
			<PopoverTrigger
				id={id}
				nativeButton={false}
				disabled={disabled}
				render={
					<div className="relative">
						<Input
							type="text"
							value={inputValue}
							placeholder={use24HourTime ? "HH:mm" : "hh:mm AM"}
							className="w-32 bg-background pr-8"
							disabled={disabled}
							onPointerDown={(e) => {
								e.stopPropagation();
							}}
							onFocus={() => {
								setOpen(true);
							}}
							onClick={(e) => {
								e.stopPropagation();
								setOpen(true);
							}}
							onChange={(e) => {
								const nextInputValue = e.target.value;
								const parsedValue = parseDisplayTime(nextInputValue);
								setInputValue(nextInputValue);
								setDraftValue(nextInputValue);
								onChange(
									parsedValue ??
										(nextInputValue.trim() ? nextInputValue : undefined),
								);
							}}
							onKeyDown={(e) => {
								if (e.key === "Enter") {
									e.preventDefault();
									commitDraftValue();
								}
								if (e.key === "Escape") {
									e.preventDefault();
									resetDraftValue();
									setOpen(false);
								}
							}}
						/>
						<Clock3Icon
							aria-hidden="true"
							className="pointer-events-none absolute right-2 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
						/>
					</div>
				}
			>
				<span className="sr-only">{translate("Select time")}</span>
			</PopoverTrigger>
			<PopoverContent
				ref={popupRef}
				align="start"
				sideOffset={6}
				initialFocus={false}
				finalFocus={false}
				className="z-[80] w-auto gap-3 p-3"
			>
				<div className="flex items-center justify-between gap-2">
					<div className="flex items-center gap-2 text-xs font-medium">
						<Clock3Icon className="size-4 text-muted-foreground" />
						<span>{translate("Time")}</span>
					</div>
					<div className="flex gap-1">
						<Button
							type="button"
							variant="secondary"
							size="sm"
							onClick={commitDraftValue}
						>
							{translate("Done")}
						</Button>
					</div>
				</div>
				<div
					className={cn(
						"grid gap-2",
						use24HourTime
							? "grid-cols-[4.5rem_4.5rem]"
							: "grid-cols-[4.5rem_4.5rem_4.5rem]",
					)}
				>
					<div className="rounded-md border">
						<div className="border-b px-2 py-1 text-center text-[11px] font-medium text-muted-foreground">
							{translate("Hour")}
						</div>
						<div className="h-48 overflow-y-auto p-1">
							{(use24HourTime ? hourColumnValues24 : hourColumnValues12).map(
								(hourValue) => {
									const selected = use24HourTime
										? timeParts.hour24 === Number.parseInt(hourValue, 10)
										: timeParts.hour12 === Number.parseInt(hourValue, 10);
									return (
										<Button
											key={hourValue}
											type="button"
											variant={selected ? "secondary" : "ghost"}
											size="sm"
											className="mb-1 w-full justify-center"
											data-time-option={`hour-${Number.parseInt(hourValue, 10)}`}
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => {
												updateTimePart(
													use24HourTime
														? { hour24: Number.parseInt(hourValue, 10) }
														: { hour12: Number.parseInt(hourValue, 10) },
												);
											}}
										>
											{hourValue}
										</Button>
									);
								},
							)}
						</div>
					</div>
					<div className="rounded-md border">
						<div className="border-b px-2 py-1 text-center text-[11px] font-medium text-muted-foreground">
							{translate("Minute")}
						</div>
						<div className="h-48 overflow-y-auto p-1">
							{minuteColumnValues.map((minuteValue) => {
								const selected =
									timeParts.minute === Number.parseInt(minuteValue, 10);
								return (
									<Button
										key={minuteValue}
										type="button"
										variant={selected ? "secondary" : "ghost"}
										size="sm"
										className="mb-1 w-full justify-center"
										data-time-option={`minute-${Number.parseInt(minuteValue, 10)}`}
										onMouseDown={(e) => e.preventDefault()}
										onClick={() => {
											updateTimePart({
												minute: Number.parseInt(minuteValue, 10),
											});
										}}
									>
										{minuteValue}
									</Button>
								);
							})}
						</div>
					</div>
					{!use24HourTime && (
						<div className="rounded-md border">
							<div className="border-b px-2 py-1 text-center text-[11px] font-medium text-muted-foreground">
								{translate("AM/PM")}
							</div>
							<div className="h-48 overflow-y-auto p-1">
								{meridiemColumnValues.map((meridiemValue) => {
									const selected = timeParts.meridiem === meridiemValue;
									return (
										<Button
											key={meridiemValue}
											type="button"
											variant={selected ? "secondary" : "ghost"}
											size="sm"
											className="mb-1 w-full justify-center"
											data-time-option={`meridiem-${meridiemValue}`}
											onMouseDown={(e) => e.preventDefault()}
											onClick={() => {
												updateTimePart({
													meridiem: meridiemValue,
												});
											}}
										>
											{meridiemValue}
										</Button>
									);
								})}
							</div>
						</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}

export function DateTimeRangePicker({
	label,
	value,
	disabled,
	disablePast,
	onChange,
	validateBeforeApply,
	infoText,
	fromLabel,
	toLabel,
	className,
	required,
	catalogDurationRules,
	use24HourTime = false,
}: {
	label: string;
	value?: {
		dateRange: DateRange | undefined;
		timeRange: TimeRange | undefined;
	};
	disabled: boolean;
	disablePast: boolean;
	onChange: (value: DateTimeRangeValue) => void;
	validateBeforeApply?: (
		value: DateTimeRangeValue,
	) => Promise<string | undefined>;
	infoText?: string;
	fromLabel?: string;
	toLabel?: string;
	className?: string;
	required?: boolean;
	catalogDurationRules?: CatalogDurationRules;
	use24HourTime?: boolean;
}) {
	let initialFromTime: string | undefined = "";
	let initialToTime: string | undefined = "";
	if (value?.timeRange?.from) {
		initialFromTime = value?.timeRange?.from || "";
	}
	if (value?.timeRange?.to) {
		initialToTime = value?.timeRange?.to || "";
	}
	const valueFromDate = value?.dateRange?.from;
	const valueToDate = value?.dateRange?.to;
	const valueTimeRange = value?.timeRange;

	const [selectedDayRange, setSelectedDayRange] = React.useState<
		DateRange | undefined
	>(value?.dateRange);
	const [fromTime, setFromTime] = React.useState<string | undefined>(
		initialFromTime,
	);
	const [toTime, setToTime] = React.useState<string | undefined>(initialToTime);
	const [open, setOpen] = React.useState(false);
	const [currentTime, setCurrentTime] = React.useState(() => dayjs());
	const [popoverSide, setPopoverSide] = React.useState<"top" | "bottom">(
		"bottom",
	);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const openTimeoutRef = React.useRef<number | null>(null);
	const pendingCommitRef = React.useRef<{
		dateRange: DateRange | undefined;
		timeRange: TimeRange | undefined;
	} | null>(null);
	const [showAvailabilityInfo, setShowAvailabilityInfo] = React.useState(false);
	const translate = useTranslate();
	const [dateRangeValidationMessage, setDateRangeValidationMessage] =
		React.useState<string>("");
	const [applyValidationMessage, setApplyValidationMessage] =
		React.useState<string>("");
	const [isApplying, setIsApplying] = React.useState(false);
	const storefrontSettingsState = useStorefrontSettingsState();
	const storefrontSettingsData = storefrontSettingsState.get().settings;

	const toDateAdjustmentDays =
		storefrontSettingsData?.ToDateAdjustmentDays || 0;
	const showReservationHourFields =
		storefrontSettingsData?.ShowReservationHourFields || false;
	const enableHoursOfOperation =
		storefrontSettingsData?.EnableHoursOfOperation || false;
	const openTime24 = storefrontSettingsData?.OpenTime || "";
	const closeTime24 = storefrontSettingsData?.CloseTime || "";
	const openTimeDisplay = formatTimeForDisplay(openTime24, use24HourTime);
	const closeTimeDisplay = formatTimeForDisplay(closeTime24, use24HourTime);
	const enableOpenDays = storefrontSettingsData?.EnableOpenDays || false;
	const openSu = storefrontSettingsData?.OpenSunday || false;
	const openMo = storefrontSettingsData?.OpenMonday || false;
	const openTu = storefrontSettingsData?.OpenTuesday || false;
	const openWe = storefrontSettingsData?.OpenWednesday || false;
	const openTh = storefrontSettingsData?.OpenThursday || false;
	const openFr = storefrontSettingsData?.OpenFriday || false;
	const openSa = storefrontSettingsData?.OpenSaturday || false;
	const minEquipmentRequestHours =
		catalogDurationRules?.minHours !== null &&
		catalogDurationRules?.minHours !== undefined &&
		catalogDurationRules.minHours > 0
			? catalogDurationRules.minHours
			: undefined;
	const maxEquipmentRequestHours =
		catalogDurationRules?.maxHours !== null &&
		catalogDurationRules?.maxHours !== undefined &&
		catalogDurationRules.maxHours > 0
			? catalogDurationRules.maxHours
			: undefined;

	let originalFromDayOfWeek = "";
	let originalFromDate = "";
	let originalFromTime = "";
	let originalToDayOfWeek = "";
	let originalToDate = "";
	let originalToTime = "";
	if (value?.dateRange) {
		if (value?.dateRange.from) {
			originalFromDayOfWeek = dayjs(value?.dateRange?.from).format("ddd");
			originalFromDate = dayjs(value?.dateRange?.from).format("ll");
			originalFromTime = formatTimeForDisplay(
				value?.timeRange?.from,
				use24HourTime,
			);
		}
		if (value?.dateRange.to) {
			originalToDayOfWeek = dayjs(value?.dateRange?.to).format("ddd");
			originalToDate = dayjs(value?.dateRange?.to).format("ll");
			originalToTime = formatTimeForDisplay(
				value?.timeRange?.to,
				use24HourTime,
			);
		}
	}

	let durationDays = "";
	const originalFromDateDayJs = dayjs(value?.dateRange?.from);
	const originalToDateDayJs = dayjs(value?.dateRange?.to);
	const isOriginalDateTimeRangeValid =
		originalFromDateDayJs.isValid() &&
		originalToDateDayJs.isValid() &&
		originalToDateDayJs.diff(originalFromDateDayJs, "day") >= 0;
	if (isOriginalDateTimeRangeValid) {
		const originalFromTimeMinutes = parseTimeToMinutes(value?.timeRange?.from);
		const originalToTimeMinutes = parseTimeToMinutes(value?.timeRange?.to);
		const hasValidTimes =
			originalFromTimeMinutes !== undefined &&
			originalToTimeMinutes !== undefined;
		const originalFromDateTime = hasValidTimes
			? originalFromDateDayJs
					.hour(Math.floor(originalFromTimeMinutes / 60))
					.minute(originalFromTimeMinutes % 60)
			: originalFromDateDayJs.startOf("day");
		const originalToDateTime = hasValidTimes
			? originalToDateDayJs
					.hour(Math.floor(originalToTimeMinutes / 60))
					.minute(originalToTimeMinutes % 60)
			: originalToDateDayJs.endOf("day");
		const durationHours = originalToDateTime.diff(
			originalFromDateTime,
			"hour",
			true,
		);
		if (hasValidTimes && durationHours > 0 && durationHours < 24) {
			const roundedDurationHours = Number.parseFloat(durationHours.toFixed(1));
			const unitLabel =
				roundedDurationHours === 1 ? translate("hour") : translate("hours");
			durationDays = `(${roundedDurationHours} ${unitLabel})`;
		} else {
			durationDays = `(${originalToDateDayJs.diff(originalFromDateDayJs, "day") + 1} ${translate("days")})`;
		}
	}

	React.useEffect(() => {
		if (!showReservationHourFields) {
			setFromTime(undefined);
			setToTime(undefined);
		}
	}, [showReservationHourFields]);

	React.useEffect(() => {
		const pendingCommit = pendingCommitRef.current;
		if (!pendingCommit) {
			return;
		}
		const pendingDates = pendingCommit.dateRange;
		const pendingTimes = pendingCommit.timeRange;
		const datesMatch =
			areDatesEqual(valueFromDate, pendingDates?.from) &&
			areDatesEqual(valueToDate, pendingDates?.to);
		const timesMatch =
			(valueTimeRange?.from ?? undefined) ===
				(pendingTimes?.from ?? undefined) &&
			(valueTimeRange?.to ?? undefined) === (pendingTimes?.to ?? undefined);
		if (datesMatch && timesMatch) {
			pendingCommitRef.current = null;
		}
	}, [valueFromDate, valueToDate, valueTimeRange]);

	React.useEffect(() => {
		if (open || pendingCommitRef.current) {
			return;
		}
		setSelectedDayRange((previousRange) => {
			if (
				areDatesEqual(previousRange?.from, valueFromDate) &&
				areDatesEqual(previousRange?.to, valueToDate)
			) {
				return previousRange;
			}
			return {
				from: valueFromDate,
				to: valueToDate,
			};
		});
		const committedFromTime = showReservationHourFields
			? valueTimeRange?.from
			: undefined;
		const committedToTime = showReservationHourFields
			? valueTimeRange?.to
			: undefined;
		setFromTime((previousTime) =>
			previousTime === committedFromTime ? previousTime : committedFromTime,
		);
		setToTime((previousTime) =>
			previousTime === committedToTime ? previousTime : committedToTime,
		);
	}, [
		open,
		valueFromDate,
		valueToDate,
		valueTimeRange,
		showReservationHourFields,
	]);

	React.useEffect(() => {
		if (open) {
			return;
		}
		if (pendingCommitRef.current) {
			return;
		}
		const today = currentTime.startOf("day");
		const now = currentTime;
		const normalizedToAdjustmentDays =
			Number.isFinite(toDateAdjustmentDays) && toDateAdjustmentDays >= 0
				? Math.floor(toDateAdjustmentDays)
				: 0;
		const openDayByWeekIndex = [
			openSu,
			openMo,
			openTu,
			openWe,
			openTh,
			openFr,
			openSa,
		];
		const openMinutes = parseTimeToMinutes(openTime24);
		const closeMinutes = parseTimeToMinutes(closeTime24);
		const nowMinutes = now.hour() * 60 + now.minute();

		const isOpenDay = (dayValue: dayjs.Dayjs) => {
			if (!enableOpenDays) {
				return true;
			}
			return openDayByWeekIndex[dayValue.day()] ?? false;
		};

		const hasReservableHoursLeft = (dayValue: dayjs.Dayjs) => {
			if (
				!showReservationHourFields ||
				!enableHoursOfOperation ||
				openMinutes === undefined ||
				closeMinutes === undefined
			) {
				return true;
			}
			if (!dayValue.isSame(today, "day")) {
				return true;
			}
			if (openMinutes <= closeMinutes) {
				return nowMinutes <= closeMinutes;
			}
			return true;
		};

		const isReservableDay = (dayValue: dayjs.Dayjs) =>
			isOpenDay(dayValue) && hasReservableHoursLeft(dayValue);

		const findNextReservableDay = (startingDay: dayjs.Dayjs) => {
			let candidate = startingDay.startOf("day");
			for (let i = 0; i < 366; i += 1) {
				if (isReservableDay(candidate)) {
					return candidate;
				}
				candidate = candidate.add(1, "day");
			}
			return undefined;
		};

		const rawFrom = valueFromDate
			? dayjs(valueFromDate).startOf("day")
			: undefined;
		const rawTo = valueToDate ? dayjs(valueToDate).startOf("day") : undefined;

		let nextFrom = rawFrom;
		let nextTo = rawTo;
		let changed = false;
		let fromDateAdjusted = false;

		if (disablePast && nextFrom?.isBefore(today, "day")) {
			nextFrom = today;
			changed = true;
			fromDateAdjusted = true;
		}

		if (nextFrom) {
			const adjustedFrom = findNextReservableDay(nextFrom);
			if (adjustedFrom && !adjustedFrom.isSame(nextFrom, "day")) {
				nextFrom = adjustedFrom;
				changed = true;
				fromDateAdjusted = true;
			}
		}

		if (nextFrom && fromDateAdjusted) {
			nextTo = nextFrom.add(normalizedToAdjustmentDays, "day");
			changed = true;
		} else if (nextFrom && nextTo) {
			const minTo = nextFrom.add(normalizedToAdjustmentDays, "day");
			if (nextTo.isBefore(minTo, "day")) {
				nextTo = minTo;
				changed = true;
			}
		} else if (nextTo && disablePast && nextTo.isBefore(today, "day")) {
			const minToFromToday = today.add(normalizedToAdjustmentDays, "day");
			nextTo = minToFromToday;
			changed = true;
		}

		if (nextTo) {
			const adjustedTo = findNextReservableDay(nextTo);
			if (adjustedTo && !adjustedTo.isSame(nextTo, "day")) {
				nextTo = adjustedTo;
				changed = true;
			}
		}

		if (!changed) {
			return;
		}

		const nextDateRange: DateRange = {
			from: nextFrom?.toDate(),
			to: nextTo?.toDate(),
		};
		setSelectedDayRange(nextDateRange);
		onChange({
			dateRange: nextDateRange,
			timeRange: valueTimeRange,
		});
	}, [
		open,
		currentTime,
		valueFromDate,
		valueToDate,
		valueTimeRange,
		onChange,
		toDateAdjustmentDays,
		disablePast,
		enableOpenDays,
		enableHoursOfOperation,
		showReservationHourFields,
		openTime24,
		closeTime24,
		openSu,
		openMo,
		openTu,
		openWe,
		openTh,
		openFr,
		openSa,
	]);

	React.useEffect(() => {
		const updateCurrentTime = () => {
			setCurrentTime(dayjs());
		};
		const handleVisibilityChange = () => {
			if (document.visibilityState === "visible") {
				updateCurrentTime();
			}
		};
		const timerId = window.setInterval(updateCurrentTime, 60_000);
		window.addEventListener("focus", updateCurrentTime);
		document.addEventListener("visibilitychange", handleVisibilityChange);
		return () => {
			window.clearInterval(timerId);
			window.removeEventListener("focus", updateCurrentTime);
			document.removeEventListener("visibilitychange", handleVisibilityChange);
		};
	}, []);

	React.useEffect(() => {
		return () => {
			if (openTimeoutRef.current !== null) {
				window.clearTimeout(openTimeoutRef.current);
			}
		};
	}, []);

	React.useEffect(() => {
		if (
			open ||
			pendingCommitRef.current ||
			!showReservationHourFields ||
			!valueTimeRange
		) {
			return;
		}

		const openMinutes = parseTimeToMinutes(openTime24);
		const closeMinutes = parseTimeToMinutes(closeTime24);
		const hoursOfOperationIsValid =
			enableHoursOfOperation &&
			openMinutes !== undefined &&
			closeMinutes !== undefined;
		const fallbackFromMinutes = hoursOfOperationIsValid ? openMinutes : 0;
		const fallbackToMinutes = hoursOfOperationIsValid
			? closeMinutes
			: 23 * 60 + 59;

		const hasFromTime = !!valueTimeRange.from;
		const hasToTime = !!valueTimeRange.to;
		let nextFromMinutes = parseTimeToMinutes(valueTimeRange.from);
		let nextToMinutes = parseTimeToMinutes(valueTimeRange.to);
		let changed = false;
		const nowMinutes = currentTime.hour() * 60 + currentTime.minute();
		const fromIsToday =
			!!valueFromDate && dayjs(valueFromDate).isSame(currentTime, "day");

		if (
			hasFromTime &&
			(nextFromMinutes === undefined ||
				(hoursOfOperationIsValid &&
					!isWithinOperatingHours(nextFromMinutes, openMinutes, closeMinutes)))
		) {
			nextFromMinutes = fallbackFromMinutes;
			changed = true;
		}

		if (
			hasToTime &&
			(nextToMinutes === undefined ||
				(hoursOfOperationIsValid &&
					!isWithinOperatingHours(nextToMinutes, openMinutes, closeMinutes)))
		) {
			nextToMinutes = fallbackToMinutes;
			changed = true;
		}

		if (
			disablePast &&
			fromIsToday &&
			hasFromTime &&
			nextFromMinutes !== undefined &&
			nextFromMinutes < nowMinutes
		) {
			const nextFromMinutesCandidate = hoursOfOperationIsValid
				? Math.max(nowMinutes, fallbackFromMinutes)
				: nowMinutes;
			if (
				!hoursOfOperationIsValid ||
				isWithinOperatingHours(
					nextFromMinutesCandidate,
					openMinutes,
					closeMinutes,
				)
			) {
				nextFromMinutes = nextFromMinutesCandidate;
				changed = true;
			}
		}

		const isSameDayRange =
			!!valueFromDate &&
			!!valueToDate &&
			dayjs(valueFromDate).isSame(dayjs(valueToDate), "day");
		if (
			isSameDayRange &&
			hasFromTime &&
			hasToTime &&
			nextFromMinutes !== undefined &&
			nextToMinutes !== undefined &&
			nextToMinutes < nextFromMinutes
		) {
			nextToMinutes = fallbackToMinutes;
			changed = true;
			if (nextToMinutes < nextFromMinutes) {
				nextFromMinutes = fallbackFromMinutes;
			}
		}

		if (!changed) {
			return;
		}

		const nextTimeRange: TimeRange = {
			from:
				hasFromTime && nextFromMinutes !== undefined
					? formatMinutesToTime(nextFromMinutes)
					: valueTimeRange.from,
			to:
				hasToTime && nextToMinutes !== undefined
					? formatMinutesToTime(nextToMinutes)
					: valueTimeRange.to,
		};

		setFromTime(nextTimeRange.from);
		setToTime(nextTimeRange.to);
		onChange({
			dateRange: {
				from: valueFromDate,
				to: valueToDate,
			},
			timeRange: nextTimeRange,
		});
	}, [
		open,
		currentTime,
		disablePast,
		showReservationHourFields,
		valueTimeRange,
		valueFromDate,
		valueToDate,
		enableHoursOfOperation,
		openTime24,
		closeTime24,
		onChange,
	]);

	React.useEffect(() => {
		const setValidationMessage = (message: string) => {
			setDateRangeValidationMessage((prev) =>
				prev === message ? prev : message,
			);
		};
		const openDayByWeekIndex = [
			openSu,
			openMo,
			openTu,
			openWe,
			openTh,
			openFr,
			openSa,
		];
		const dayNameByWeekIndex = [
			translate("Sunday"),
			translate("Monday"),
			translate("Tuesday"),
			translate("Wednesday"),
			translate("Thursday"),
			translate("Friday"),
			translate("Saturday"),
		];
		const activeFromTime = showReservationHourFields ? fromTime : undefined;
		const activeToTime = showReservationHourFields ? toTime : undefined;
		const fromDayJs = buildDateTime(selectedDayRange?.from, activeFromTime);
		const toDayJs = buildDateTime(selectedDayRange?.to, activeToTime);
		const fromTimeMinutes = parseTimeToMinutes(activeFromTime);
		const toTimeMinutes = parseTimeToMinutes(activeToTime);
		const fromDateIsValid = fromDayJs.isValid();
		const toDateIsValid = toDayJs.isValid();
		const dateTimeRangeIsValid =
			fromDateIsValid &&
			toDateIsValid &&
			(fromDayJs.isBefore(toDayJs) || fromDayJs.isSame(toDayJs));
		// console.log({
		//     fromDayJs,
		//     toDayJs,
		//     fromDateIsValid,
		//     toDateIsValid,
		//     isBefore: fromDayJs.isBefore(toDayJs),
		//     isSame: fromDayJs.isSame(toDayJs)
		// })
		if (!dateTimeRangeIsValid) {
			//console.log("invalid");
			setValidationMessage(translate("Invalid date range"));
			return;
		} else {
			//console.log("valid");
		}

		if (disablePast && fromDateIsValid) {
			const nowBoundary =
				showReservationHourFields && activeFromTime
					? currentTime.startOf("minute")
					: currentTime.startOf("day");
			if (fromDayJs.isBefore(nowBoundary)) {
				setValidationMessage(
					translate("Start date/time must not be in the past"),
				);
				return;
			}
		}

		if (
			(minEquipmentRequestHours !== undefined ||
				maxEquipmentRequestHours !== undefined) &&
			fromDateIsValid &&
			toDateIsValid &&
			activeFromTime &&
			activeToTime &&
			fromTimeMinutes !== undefined &&
			toTimeMinutes !== undefined
		) {
			const durationHours = toDayJs.diff(fromDayJs, "hour", true);
			if (
				minEquipmentRequestHours !== undefined &&
				durationHours < minEquipmentRequestHours
			) {
				setValidationMessage(
					translate("Reservation must be at least ") +
						minEquipmentRequestHours.toString() +
						" " +
						(minEquipmentRequestHours === 1
							? translate("hour")
							: translate("hours")),
				);
				return;
			}
			if (
				maxEquipmentRequestHours !== undefined &&
				durationHours > maxEquipmentRequestHours
			) {
				setValidationMessage(
					translate("Reservation must be no more than ") +
						maxEquipmentRequestHours.toString() +
						" " +
						(maxEquipmentRequestHours === 1
							? translate("hour")
							: translate("hours")),
				);
				return;
			}
		}

		if (enableOpenDays) {
			const fromDayOfWeek = fromDayJs.day();
			const isFromDayOfWeekValid = openDayByWeekIndex[fromDayOfWeek] ?? false;
			if (!isFromDayOfWeekValid) {
				setValidationMessage(
					translate("Invalid from date. The shop is closed on ") +
						(dayNameByWeekIndex[fromDayOfWeek] ?? ""),
				);
				return;
			}

			const toDayOfWeek = toDayJs.day();
			const isToDayOfWeekValid = openDayByWeekIndex[toDayOfWeek] ?? false;
			if (!isToDayOfWeekValid) {
				setValidationMessage(
					translate("Invalid to date. The shop is closed on ") +
						(dayNameByWeekIndex[toDayOfWeek] ?? ""),
				);
				return;
			}
		}

		if (
			showReservationHourFields &&
			enableHoursOfOperation &&
			(activeFromTime || activeToTime)
		) {
			const openMinutes = parseTimeToMinutes(openTime24);
			const closeMinutes = parseTimeToMinutes(closeTime24);
			if (openMinutes === undefined || closeMinutes === undefined) {
				setValidationMessage(
					translate("Unable to validate times against hours of operation"),
				);
				return;
			}

			if (activeFromTime) {
				if (fromTimeMinutes === undefined) {
					setValidationMessage(translate("Invalid start time"));
					return;
				}
				if (
					!isWithinOperatingHours(fromTimeMinutes, openMinutes, closeMinutes)
				) {
					setValidationMessage(
						`${translate("Start time must be within hours of operation")}: ${openTimeDisplay} - ${closeTimeDisplay}`,
					);
					return;
				}
			}

			if (activeToTime) {
				if (toTimeMinutes === undefined) {
					setValidationMessage(translate("Invalid end time"));
					return;
				}
				if (!isWithinOperatingHours(toTimeMinutes, openMinutes, closeMinutes)) {
					setValidationMessage(
						`${translate("End time must be within hours of operation")}: ${openTimeDisplay} - ${closeTimeDisplay}`,
					);
					return;
				}
			}
		}

		setValidationMessage("");
	}, [
		selectedDayRange,
		fromTime,
		toTime,
		openTime24,
		closeTime24,
		openTimeDisplay,
		closeTimeDisplay,
		openSu,
		openMo,
		openTu,
		openWe,
		openTh,
		openFr,
		openSa,
		enableOpenDays,
		enableHoursOfOperation,
		showReservationHourFields,
		minEquipmentRequestHours,
		maxEquipmentRequestHours,
		currentTime,
		disablePast,
		translate,
	]);

	const prepareOpenState = () => {
		setApplyValidationMessage("");
		setSelectedDayRange(value?.dateRange);
		if (showReservationHourFields) {
			setFromTime(value?.timeRange?.from);
			setToTime(value?.timeRange?.to);
		} else {
			setFromTime(undefined);
			setToTime(undefined);
		}
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (openTimeoutRef.current !== null) {
			window.clearTimeout(openTimeoutRef.current);
			openTimeoutRef.current = null;
		}

		if (!nextOpen) {
			// Draft reservation changes are dismissed only by the explicit
			// Apply and Cancel actions in the popover.
			return;
		}

		prepareOpenState();

		const triggerRect = containerRef.current
			?.querySelector<HTMLElement>("#date-picker")
			?.getBoundingClientRect();
		if (triggerRect) {
			const viewportHeight = window.innerHeight;
			const spaceBelow = viewportHeight - triggerRect.bottom;
			const spaceAbove = triggerRect.top;
			setPopoverSide(
				spaceBelow < 360 && spaceAbove > spaceBelow ? "top" : "bottom",
			);
		} else {
			setPopoverSide("bottom");
		}

		const isMobileViewport = window.matchMedia("(max-width: 640px)").matches;
		setShowAvailabilityInfo(!isMobileViewport);
		if (!isMobileViewport) {
			setOpen(true);
			return;
		}

		containerRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});
		openTimeoutRef.current = window.setTimeout(() => {
			setOpen(true);
			openTimeoutRef.current = null;
		}, 250);
	};

	const availabilityInfo = enableHoursOfOperation && (
		<div className="text-center">
			{enableHoursOfOperation && (
				<>
					<div className="text-xs font-bold">
						{translate("Hours of Operation")}:
					</div>
					<div className={enableOpenDays ? "pb-1" : undefined}>
						{openTimeDisplay} - {closeTimeDisplay}
					</div>
				</>
			)}
		</div>
	);

	return (
		<div ref={containerRef} className={cn(["py-1", className])}>
			<div className="flex py-2 pb-1">
				<Label
					htmlFor="date-picker"
					className="flex-1 md:w-auto whitespace-nowrap flex items-center"
				>
					{label} {required && <span className="text-destructive">*</span>}
					{infoText && (
						<Tooltip>
							<TooltipTrigger>
								<InfoIcon size={16} />
							</TooltipTrigger>
							<TooltipContent>
								<p>{infoText}</p>
							</TooltipContent>
						</Tooltip>
					)}
				</Label>
				<div
					className="text-xs text-right"
					css={css`flex: 0 0 auto;padding:0 .5rem;`}
				>
					{durationDays}
				</div>
			</div>
			<div className="flex gap-1">
				<Popover open={open} onOpenChange={handleOpenChange}>
					<PopoverTrigger
						id="date-picker"
						aria-invalid={!open && !!dateRangeValidationMessage}
						className={cn(
							buttonVariants({ variant: "outline" }),
							"w-full h-10 justify-between font-normal",
							!open &&
								!!dateRangeValidationMessage &&
								"border-destructive text-destructive ring-1 ring-destructive/25",
						)}
						disabled={disabled}
					>
						<div className="text-xs text-left p-1 grid grid-cols-[auto_auto_1fr_auto]">
							<div>{fromLabel || translate("From")}:</div>
							<div className="pl-2">
								{originalFromDayOfWeek}
								{originalFromDayOfWeek ? "," : ""}
							</div>
							<div className="px-2">{originalFromDate}</div>
							<div>{showReservationHourFields ? originalFromTime : ""}</div>

							<div>{toLabel || translate("To")}:</div>
							<div className="pl-2">
								{originalToDayOfWeek}
								{originalToDayOfWeek ? "," : ""}
							</div>
							<div className="px-2">{originalToDate}</div>
							<div>{showReservationHourFields ? originalToTime : ""}</div>
						</div>
						<ChevronDownIcon />
						{/* <Calendar1Icon /> */}
					</PopoverTrigger>
					<PopoverContent
						side={popoverSide}
						className={cn([
							showReservationHourFields ? "max-w-85" : "max-w-65",
							"flex w-[calc(100vw-1rem)] max-w-[calc(100vw-1rem)] max-h-[min(var(--available-height),calc(100dvh-1rem))] overflow-y-auto overscroll-contain px-3 pb-4 sm:w-auto sm:max-h-[min(42rem,var(--available-height),calc(100dvh-2rem))] sm:px-6 md:max-w-[42rem]",
						])}
						align="start"
					>
						{availabilityInfo && (
							<>
								<div className="hidden sm:block">{availabilityInfo}</div>
								<Collapsible
									open={showAvailabilityInfo}
									onOpenChange={setShowAvailabilityInfo}
									className="sm:hidden"
								>
									<CollapsibleTrigger
										className={cn(
											buttonVariants({ variant: "outline" }),
											"w-full justify-between text-xs",
										)}
									>
										<span>{translate("Store Availability Info")}</span>
										<ChevronDownIcon
											className={cn(
												"size-4 transition-transform",
												showAvailabilityInfo && "rotate-180",
											)}
										/>
									</CollapsibleTrigger>
									<CollapsibleContent className="pt-2">
										{availabilityInfo}
									</CollapsibleContent>
								</Collapsible>
							</>
						)}
						<div className="pt-1">
							<CompactRangeCalendar
								range={selectedDayRange}
								onFromSelect={(nextFrom) => {
									setSelectedDayRange((currentRange) => ({
										from: nextFrom,
										to:
											nextFrom &&
											currentRange?.to &&
											dayjs(currentRange.to).isBefore(nextFrom, "day")
												? nextFrom
												: currentRange?.to,
									}));
								}}
								onToSelect={(nextTo) => {
									setSelectedDayRange((currentRange) => ({
										from:
											nextTo &&
											currentRange?.from &&
											dayjs(nextTo).isBefore(currentRange.from, "day")
												? nextTo
												: currentRange?.from,
										to: nextTo,
									}));
								}}
								disablePast={disablePast}
								enableOpenDays={enableOpenDays}
								openDays={[
									openSu,
									openMo,
									openTu,
									openWe,
									openTh,
									openFr,
									openSa,
								]}
								fromLabel={fromLabel || translate("From")}
								toLabel={toLabel || translate("To")}
							/>
						</div>
						<div>
							<div
								className={cn(
									"mx-auto grid items-center text-sm text-left gap-2",
									showReservationHourFields
										? "grid-cols-[auto_minmax(0,1fr)_auto]"
										: "grid-cols-[auto_minmax(0,1fr)]",
								)}
							>
								<div className="flex items-center">{translate("From")}:</div>
								<DateField
									id="from-date-picker-popover"
									value={selectedDayRange?.from}
									disabled={disabled}
									disablePast={disablePast}
									onChange={(nextValue) => {
										setSelectedDayRange((range) => ({
											from: nextValue,
											to: range?.to,
										}));
									}}
								/>
								{showReservationHourFields && (
									<div className="flex items-center">
										<TimeField
											id="from-time-picker-popover"
											value={fromTime}
											disabled={disabled}
											translate={translate}
											use24HourTime={use24HourTime}
											onChange={(nextValue) => {
												setFromTime(nextValue);
											}}
										/>
									</div>
								)}

								<div className="flex items-center">{translate("To")}:</div>
								<DateField
									id="to-date-picker-popover"
									value={selectedDayRange?.to}
									disabled={disabled}
									disablePast={disablePast}
									onChange={(nextValue) => {
										setSelectedDayRange((range) => ({
											from: range?.from,
											to: nextValue,
										}));
									}}
								/>
								{showReservationHourFields && (
									<div className="flex items-center">
										<TimeField
											id="to-time-picker-popover"
											value={toTime}
											disabled={disabled}
											translate={translate}
											use24HourTime={use24HourTime}
											onChange={(nextValue) => {
												setToTime(nextValue);
											}}
										/>
									</div>
								)}
							</div>
						</div>

						<div className="flex justify-end gap-2">
							<Button
								variant="outline"
								onClick={() => {
									prepareOpenState();
									setOpen(false);
								}}
							>
								{translate("Cancel")}
							</Button>
							<Button
								disabled={!!dateRangeValidationMessage || isApplying}
								onClick={async () => {
									if (dateRangeValidationMessage) {
										return;
									}
									const nextRange = {
										dateRange: {
											from: selectedDayRange?.from
												? dayjs(selectedDayRange.from).startOf("day").toDate()
												: undefined,
											to: selectedDayRange?.to
												? dayjs(selectedDayRange.to).startOf("day").toDate()
												: undefined,
										},
										timeRange: {
											from: showReservationHourFields ? fromTime : undefined,
											to: showReservationHourFields ? toTime : undefined,
										},
									};
									setApplyValidationMessage("");
									setIsApplying(true);
									try {
										const validationMessage =
											await validateBeforeApply?.(nextRange);
										if (validationMessage) {
											setApplyValidationMessage(validationMessage);
											return;
										}
										pendingCommitRef.current = nextRange;
										setOpen(false);
										onChange(nextRange);
									} finally {
										setIsApplying(false);
									}
								}}
							>
								{isApplying && <LoaderCircle className="size-4 animate-spin" />}
								{translate("Apply")}
							</Button>
						</div>
						{!!dateRangeValidationMessage && (
							<Alert>
								<InfoIcon color="var(--color-red-400)" />
								<AlertTitle className="text-red-400"></AlertTitle>
								<AlertDescription className="max-w-55">
									{dateRangeValidationMessage}
								</AlertDescription>
							</Alert>
						)}
						{!!applyValidationMessage && (
							<Alert variant="destructive">
								<InfoIcon />
								<AlertDescription className="max-w-55">
									{applyValidationMessage}
								</AlertDescription>
							</Alert>
						)}
					</PopoverContent>
				</Popover>
			</div>
			{!open && !!dateRangeValidationMessage && (
				<Alert variant="destructive" className="mt-1">
					<InfoIcon />
					<AlertTitle>{translate("Reservation Dates")}</AlertTitle>
					<AlertDescription>{dateRangeValidationMessage}</AlertDescription>
				</Alert>
			)}
		</div>
	);
}
